/**
 * Chart widget — config shape and helpers (Amendment A: each chart is its
 * own widget; the chart-row container is gone).
 */

import { DEFAULT_CHART_PERIOD, type ChartPeriod } from '../../../pages/dslAgentTest/chartPeriod';
import {
  DEFAULT_LINE_STYLE,
  DEFAULT_LINE_WIDTH,
  pickTagColor,
} from '../../../pages/dslAgentTest/colorPalette';
import type { DslTag } from '../../../pages/dslAgentTest/types';
import { newId } from '../dashboard/types';

export const CHART_WIDGET_TYPE = 'chart';

export interface ChartWidgetConfig {
  chartId: string;
  title: string;
  period: ChartPeriod;
  tags: DslTag[];
}

export function isChartConfig(value: unknown): value is ChartWidgetConfig {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<ChartWidgetConfig>;
  return (
    typeof v.chartId === 'string' &&
    typeof v.title === 'string' &&
    typeof v.period === 'string' &&
    Array.isArray(v.tags)
  );
}

/**
 * Build a fresh chart-widget config. If `seedDsls` is supplied (legend
 * drag-drop — Q15 — or the Excel "Chart" toolbar action with multiple
 * DSL cells selected), each entry becomes a tag with a distinct color
 * picked from the palette.
 *
 * Title defaults to "Chart" per Q16.
 */
export function createInitialChartConfig(seedDsls?: string[]): ChartWidgetConfig {
  const trimmed = (seedDsls ?? [])
    .map((dsl) => dsl?.trim() ?? '')
    .filter((dsl) => dsl.length > 0);
  const tags: DslTag[] = trimmed.map((dsl, index) => ({
    id: newId('tag'),
    dsl,
    color: pickTagColor(index),
    lineStyle: DEFAULT_LINE_STYLE,
    lineWidth: DEFAULT_LINE_WIDTH,
  }));
  return {
    chartId: newId('chart'),
    title: 'Chart',
    period: DEFAULT_CHART_PERIOD,
    tags,
  };
}
